import { useApolloClient } from '@apollo/client';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

const rootQueryShape = z.object({
  ROOT_QUERY: z.record(z.string(), z.unknown()),
});

/**
 * One root field's value, when it is a list of records the sweep can read.
 *
 * Parsed per field rather than across the whole of `ROOT_QUERY`. The original
 * required *every* value to be such a list, and a real `ROOT_QUERY` always
 * carries `__typename: 'Query'` — a string — so the parse failed and the sweep
 * evicted nothing at all. Skipping the fields it cannot read is what the sweep
 * was always meant to do.
 */
const cachedRecordList = z.object({ Id: z.string().optional() }).array();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Whether a cached tanstack result holds the parent anywhere inside it.
 *
 * The Apollo leg gets to assume one shape — `ROOT_QUERY.<field>[]` — because
 * that cache is normalised. A tanstack cache holds whatever each procedure
 * returned, so matching the same set of queries means walking the value.
 *
 * A query whose shape hides the id (inside a `Map`, or keyed by something
 * other than `Id`) is not invalidated. That is the cost of narrowing: the
 * previous unfiltered call could not miss.
 */
const containsId = (data: unknown, parentId: string): boolean => {
  if (Array.isArray(data)) {
    return data.some((item) => containsId(item, parentId));
  }
  if (!isRecord(data)) {
    return false;
  }
  if (data.Id === parentId) {
    return true;
  }

  return Object.values(data).some((value) => containsId(value, parentId));
};

/**
 * Refreshes everything cached about a parent entity after it changed
 * server-side without this client having caused it (COI-577).
 *
 * The one caller is `ChangeRequestLevels`, when the selected change request
 * leaves `Pending` — another approver resolved it. There is no mutation here,
 * so there is no mutation hook to hang the invalidation on.
 *
 * Apollo leg: the panel's original ROOT_QUERY sweep — evict every root field
 * holding an item whose `Id` is the parent's. Moved across unchanged except for
 * the per-field parse, without which it never evicted anything (see
 * `cachedRecordList`).
 *
 * tRPC leg: `invalidateQueries` narrowed by a predicate that matches the same
 * thing the Apollo sweep does — a cached result holding this parent. It was
 * unfiltered first; that marked every cached query stale, not just the mounted
 * ones it refetched, so the whole session's cache went cold after one approval.
 */
export const useClearParentFromCache = (parentId: string): (() => void) => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const client = useApolloClient();
  const queryClient = useQueryClient();

  return useCallback(() => {
    if (trpcEnabled) {
      void queryClient.invalidateQueries({
        predicate: (query) => containsId(query.state.data, parentId),
      });

      return;
    }

    const rootCache = rootQueryShape.safeParse(client.cache.extract());
    if (!rootCache.success) {
      return;
    }
    const keys = Object.entries(rootCache.data.ROOT_QUERY).filter(
      ([, value]) => {
        const records = cachedRecordList.safeParse(value);

        return (
          records.success && records.data.some((item) => item.Id === parentId)
        );
      }
    );
    keys.forEach(([key]) => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: key });
      client.cache.gc();
    });
  }, [trpcEnabled, client, queryClient, parentId]);
};
